// src/auth/AuthContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";

const AuthContext = createContext(null);

// Central AUTH endpoint (login/logout/refresh), not the tenant data server.
export const AUTH_API_BASE =
  "https://mydata.tablemate.net/redir.php?url=https://mydata.tablemate.net/api";

const SESSION_KEY_SELECTED_RESTAURANT = "selectedRestaurantId";

// 15 minutes idle timeout (in ms)
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
// Show warning 1 minute before logout
const WARNING_BEFORE_LOGOUT_MS = 60 * 1000;
// Shared across tabs so activity in any tab keeps the session alive
// (logout.php ends the server session for every tab).
const LAST_ACTIVITY_KEY = "tm_last_activity";
// Don't write the activity timestamp more often than this
const ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000;

function readLastActivity() {
  try {
    return Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeLastActivity(ts) {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(ts));
  } catch {
    // storage unavailable — the in-memory timestamp still works for this tab
  }
}

// Helper to parse CSV list of restaurant ids from user
function getRestaurantIdsFromUser(u) {
  if (!u) return [];

  // Support both `restaurants_id` and `restaurant_ids`
  const raw = u.restaurants_id ?? u.restaurant_ids;
  if (!raw) return [];

  return String(raw)
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);        // { id, email, role, restaurants_id, restaurants: [{id, server}, ...] }
  const [accessToken, setAccessToken] = useState(null);
  const [viewAsEmail, setViewAsEmail] = useState("");
  const [loading, setLoading] = useState(true);  // true while restoring session

  const [showIdleWarning, setShowIdleWarning] = useState(false);

  // -----------------------------------
  // Initialize selectedRestaurantId
  // -----------------------------------
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(() => {
    // Prefer explicitly saved selected id if present
    const storedSelected = sessionStorage.getItem(SESSION_KEY_SELECTED_RESTAURANT);
    if (storedSelected) return storedSelected;

    // Otherwise, try to derive from stored user
    const savedUser = sessionStorage.getItem("user");
    if (!savedUser) return null;

    try {
      const parsed = JSON.parse(savedUser);
      const ids = getRestaurantIdsFromUser(parsed);
      return ids[0] || null;
    } catch {
      return null;
    }
  });

  // -----------------------------------
  // Restore session from sessionStorage
  // -----------------------------------
  useEffect(() => {
    const savedToken = sessionStorage.getItem("accessToken");
    const savedUser  = sessionStorage.getItem("user");

    if (savedToken && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setAccessToken(savedToken);
        setUser(parsedUser);

        const ids = getRestaurantIdsFromUser(parsedUser);
        const storedSelected = sessionStorage.getItem(SESSION_KEY_SELECTED_RESTAURANT);

        // If we have a stored selected ID and it's still valid, use it
        let effectiveSelected = null;
        if (storedSelected && ids.includes(storedSelected)) {
          effectiveSelected = storedSelected;
        } else if (ids.length > 0) {
          // Otherwise default to first available
          effectiveSelected = ids[0];
        }

        setSelectedRestaurantId(effectiveSelected || null);
        setViewAsEmail(sessionStorage.getItem("viewAsEmail") || "");
      } catch {
        sessionStorage.removeItem("accessToken");
        sessionStorage.removeItem("user");
        sessionStorage.removeItem(SESSION_KEY_SELECTED_RESTAURANT);
        sessionStorage.removeItem("viewAsEmail");
      }
    }

    setLoading(false);
  }, []);

  // ------------------------
  // Login (email + password)
  // ------------------------
  const login = async (email, password) => {
    const res = await fetch(`${AUTH_API_BASE}/auth/login.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include", // allows refreshToken cookie to be set
      body: JSON.stringify({ email, password }),
    });

    const raw = await res.text();
    let data;
    try {
      data = JSON.parse(raw);
    } catch {
      throw new Error(
        res.ok
          ? "Login service is unavailable. Please try again later."
          : "Login failed"
      );
    }

    if (!res.ok) {
      throw new Error(data?.message || "Login failed");
    }

    applySession(data);
    sessionStorage.removeItem("viewAsEmail");
    setViewAsEmail("");
  };

  const applySession = (data) => {
    setAccessToken(data.accessToken);
    setUser(data.user);

    const ids = getRestaurantIdsFromUser(data.user);
    const defaultId = ids[0] || null;

    setSelectedRestaurantId(defaultId);
    if (defaultId) {
      sessionStorage.setItem(
        SESSION_KEY_SELECTED_RESTAURANT,
        String(defaultId)
      );
    } else {
      sessionStorage.removeItem(SESSION_KEY_SELECTED_RESTAURANT);
    }

    sessionStorage.setItem("accessToken", data.accessToken);
    sessionStorage.setItem("user", JSON.stringify(data.user));
  };

  const startViewAs = (data) => {
    applySession(data);
    const email = data?.user?.email || "";
    sessionStorage.setItem("viewAsEmail", email);
    setViewAsEmail(email);
  };

  // ------------- Logout -------------
  const logout = useCallback(async () => {
    try {
      await fetch(`${AUTH_API_BASE}/auth/logout.php`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.warn("[Auth] Logout error", e);
    }

    setAccessToken(null);
    setUser(null);
    setSelectedRestaurantId(null);
    setShowIdleWarning(false);

    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem(SESSION_KEY_SELECTED_RESTAURANT);
    sessionStorage.removeItem("viewAsEmail");
    setViewAsEmail("");
  }, []);

  // -------------------------------------------------
  // Optional: refresh access token using refresh.php
  // -------------------------------------------------
  const refreshAccessToken = async () => {
    try {
      const res = await fetch(`${AUTH_API_BASE}/auth/refresh.php`, {
        method: "POST",
        credentials: "include",
      });

      if (!res.ok) {
        console.warn("[Auth] refresh failed:", res.status, res.statusText);
        // Session is not valid anymore → log out (will trigger redirect)
        await logout();
        return;
      }

      const data = await res.json(); // { accessToken, user }
      if (data?.accessToken && data?.user) {
        setAccessToken(data.accessToken);
        setUser(data.user);
        sessionStorage.setItem("accessToken", data.accessToken);
        sessionStorage.setItem("user", JSON.stringify(data.user));

        const ids = getRestaurantIdsFromUser(data.user);
        const storedSelected = sessionStorage.getItem(SESSION_KEY_SELECTED_RESTAURANT);

        let effectiveSelected = null;
        if (storedSelected && ids.includes(storedSelected)) {
          effectiveSelected = storedSelected;
        } else if (ids.length > 0) {
          effectiveSelected = ids[0];
        }

        setSelectedRestaurantId(effectiveSelected || null);

        if (effectiveSelected) {
          sessionStorage.setItem(
            SESSION_KEY_SELECTED_RESTAURANT,
            String(effectiveSelected)
          );
        } else {
          sessionStorage.removeItem(SESSION_KEY_SELECTED_RESTAURANT);
        }
      }
    } catch (e) {
      console.warn("[Auth] refresh error", e);
      // Network/other error → treat as logged out
      await logout();
    }
  };

  // -----------------------------------------
  // 15-minute inactivity (idle) logout
  // with 1-minute warning popup.
  // Timers only schedule a *check*; we log out only if the last recorded
  // activity (in this or any other tab) is really older than the timeout.
  // -----------------------------------------
  const lastActivityRef = useRef(Date.now());
  const recordActivityRef = useRef(() => {});

  useEffect(() => {
    // Only run idle logic when user is logged in
    if (!user || !accessToken) return;

    let checkTimer = null;
    let lastWrite = 0;

    const getLastActivity = () =>
      Math.max(lastActivityRef.current, readLastActivity());

    const scheduleCheck = (delay) => {
      if (checkTimer) clearTimeout(checkTimer);
      checkTimer = setTimeout(checkIdle, Math.max(delay, 1000));
    };

    const checkIdle = () => {
      const idleFor = Date.now() - getLastActivity();

      if (idleFor >= IDLE_TIMEOUT_MS) {
        console.log("[Auth] No user activity for 15 minutes → logging out");
        logout();
        return;
      }

      if (idleFor >= IDLE_TIMEOUT_MS - WARNING_BEFORE_LOGOUT_MS) {
        setShowIdleWarning(true);
        scheduleCheck(IDLE_TIMEOUT_MS - idleFor);
        return;
      }

      // Activity happened since the timer was set → not idle, keep going
      setShowIdleWarning(false);
      scheduleCheck(IDLE_TIMEOUT_MS - WARNING_BEFORE_LOGOUT_MS - idleFor);
    };

    const recordActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      if (now - lastWrite >= ACTIVITY_WRITE_THROTTLE_MS) {
        lastWrite = now;
        writeLastActivity(now);
      }
      setShowIdleWarning((shown) => (shown ? false : shown));
    };
    recordActivityRef.current = recordActivity;

    // Another tab recorded activity
    const onStorage = (e) => {
      if (e.key === LAST_ACTIVITY_KEY) setShowIdleWarning(false);
    };

    // Timers are throttled in background tabs / during sleep, so re-check
    // as soon as the tab becomes visible again.
    const onVisibility = () => {
      if (document.visibilityState === "visible") checkIdle();
    };

    const activityEvents = [
      "mousemove",
      "mousedown",
      "keydown",
      "wheel",
      "scroll",
      "touchstart",
      "pointerdown",
      "input",
    ];

    // Capture phase so scrolls inside inner containers (which don't bubble)
    // and events stopped by child handlers still count as activity.
    const listenerOpts = { capture: true, passive: true };
    activityEvents.forEach((event) =>
      window.addEventListener(event, recordActivity, listenerOpts)
    );
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", onVisibility);

    // Logging in / restoring a session counts as activity
    recordActivity();
    scheduleCheck(IDLE_TIMEOUT_MS - WARNING_BEFORE_LOGOUT_MS);

    return () => {
      if (checkTimer) clearTimeout(checkTimer);
      recordActivityRef.current = () => {};
      activityEvents.forEach((event) =>
        window.removeEventListener(event, recordActivity, listenerOpts)
      );
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user, accessToken, logout]);

  // User clicked "Stay logged in" in the warning popup
  const handleStayLoggedIn = () => {
    recordActivityRef.current();
    setShowIdleWarning(false);
  };

  const handleLogoutNow = () => {
    logout();
  };

  // -----------------------------------------
  // Multi-tenant helpers (from PHP response)
  // -----------------------------------------

  const restaurantIds = getRestaurantIdsFromUser(user);

  // Keep selectedRestaurantId in sync if user changes
  useEffect(() => {
    if (!user) {
      // On a page refresh `user` is briefly null before the session is restored. Don't wipe the saved
      // selection during that window — only clear it once we're genuinely logged out (not loading).
      if (!loading) {
        setSelectedRestaurantId(null);
        sessionStorage.removeItem(SESSION_KEY_SELECTED_RESTAURANT);
      }
      return;
    }

    const ids = restaurantIds;
    if (!ids.length) {
      setSelectedRestaurantId(null);
      sessionStorage.removeItem(SESSION_KEY_SELECTED_RESTAURANT);
      return;
    }

    setSelectedRestaurantId((prev) => {
      // if we already have a valid selection, keep it
      if (prev && ids.map(String).includes(String(prev))) return String(prev);
      // otherwise default to first
      return ids[0] || null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.restaurants_id, user?.restaurant_ids, loading]);

  // Persist selectedRestaurantId any time it changes (while logged in)
  useEffect(() => {
    if (!user) {
      // Same as above: don't clear the saved selection while the session is still restoring on refresh.
      if (!loading) sessionStorage.removeItem(SESSION_KEY_SELECTED_RESTAURANT);
      return;
    }

    if (selectedRestaurantId) {
      sessionStorage.setItem(
        SESSION_KEY_SELECTED_RESTAURANT,
        String(selectedRestaurantId)
      );
    } else {
      sessionStorage.removeItem(SESSION_KEY_SELECTED_RESTAURANT);
    }
  }, [user, selectedRestaurantId, loading]);

  // Use selectedRestaurantId if set; otherwise fall back to first
  const primaryRestaurantId =
    selectedRestaurantId || restaurantIds[0] || null;

  const restaurants = Array.isArray(user?.restaurants) ? user.restaurants : [];

  const primaryRestaurant =
    restaurants.find(
      (r) => primaryRestaurantId && String(r.id) === String(primaryRestaurantId)
    ) || restaurants[0] || null;

  console.log("AUTH primaryRestaurantId:", primaryRestaurantId);

  // Tenant data server URL (e.g. https://wazzzasroom.tablemate.net)
  const tenantServerUrl = primaryRestaurant?.server || null;

  const value = {
    user,
    accessToken,
    loading,
    isAuthenticated: !!user,

    login,
    logout,
    startViewAs,
    viewAsEmail,
    refreshAccessToken,

    // multi-tenant details
    restaurantIds,
    restaurants,
    primaryRestaurant,
    primaryRestaurantId,
    tenantServerUrl,

    // tenant switching
    selectedRestaurantId,
    setSelectedRestaurantId,
    setPrimaryRestaurantId: setSelectedRestaurantId,

    // exposed in case you want interceptors, etc.
    setAccessToken,
    setUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}

      {showIdleWarning && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              padding: "24px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: "8px" }}>
              You&#39;re about to be logged out
            </h3>
            <p style={{ marginBottom: "16px" }}>
              You&#39;ve been inactive for a while. You will be logged out in about
              one minute unless you choose to stay logged in.
            </p>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              <button
                onClick={handleLogoutNow}
                style={{
                  padding: "8px 14px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  background: "#f5f5f5",
                  cursor: "pointer",
                }}
              >
                Log out now
              </button>
              <button
                onClick={handleStayLoggedIn}
                style={{
                  padding: "8px 14px",
                  borderRadius: "4px",
                  border: "none",
                  background: "red",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Stay logged in
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
